import { useMatch } from "react-router-dom";
import { useAuth } from "@/auth/AuthProvider";
import { useMe, type Household } from "@/api/households";

/**
 * The household named by the current route, as the API reported it.
 *
 * It is read from `me` rather than fetched per route, because `me` already
 * carries the caller's households *and their role in each* — and the role is
 * what decides whether a write button should be there at all. Taking the
 * household from the URL alone would give a name but no authority.
 *
 * Undefined means the route names a household the caller is not a member of.
 * The API answers that with a 404 rather than a 403 (K-08), and so does this:
 * confirming that a household exists is itself information.
 */
export function useHousehold(): Household | undefined {
  const householdId = useHouseholdId();
  const { isAuthenticated } = useAuth();
  const { data } = useMe(isAuthenticated);
  return data?.households.find((h) => h.id === householdId);
}

/**
 * The household id in the current path.
 *
 * `useMatch` rather than `useParams` because the layout sits *above* the
 * household routes in the tree: `useParams` only sees the parameters of the
 * route that matched where it is called, which for the layout is none of them.
 */
export function useHouseholdId(): string | undefined {
  return useMatch("/households/:householdId/*")?.params.householdId;
}

/** True when the caller may write in this household (`model.md` §3.2). */
export function useCanWrite(): boolean {
  const household = useHousehold();
  return household?.role === "owner" || household?.role === "member";
}
